import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { PurchaseNoteModal, PurchaseNotesList } from './PurchaseNotes';
import ItemDescriptionInput, { mergeGrnLines } from './ItemDescriptionInput';
import { lineEdit, rateFromAmount } from '../utils/lineAmounts';
import GoodsReceiptChoice, { GoodsStatusBadge, GoodsToArriveButton, emptyGoodsReceipt, goodsReceiptProblem } from './GoodsReceiptChoice';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
import { formatDate } from '../utils/dateFormat';
const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};
// A unit price in full: paise, and the further decimals a vendor's rate has (up to four)
const rateText = (n) => {
  const r = Number(n.toFixed(4));
  return r.toFixed(Math.max(2, (String(r).split('.')[1] || '').length));
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const emptyLine = () => ({
  description: '', itemId: null, unitOfMeasurementId: null, unitSymbol: '', hsnCode: '', quantity: '', unitPrice: '', driver: 'taxable', taxableAmount: '', taxRatePercent: '18',
  itcEligible: true, itcIneligibleReason: '', exact: false, cgstAmount: '', sgstAmount: '', igstAmount: ''
});

const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};

/**
 * Vendor tax invoices recorded for input tax credit. Goods already received through GRNs stay on the
 * payable at their GRN value; the invoice adds the GST (claimable or not) and any value difference.
 */
function PurchaseInvoices({ active }) {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [returning, setReturning] = useState(null);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setLoading(true);
    axios.get('/api/purchase-invoices')
      // Purchases with no GST have their own tab
      .then((r) => { if (!cancelled) setInvoices((Array.isArray(r.data) ? r.data : []).filter((i) => !i.nonGst)); })
      .catch(() => { if (!cancelled) toast.error('Error fetching purchase invoices'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [active, reloadKey]);

  useEffect(() => {
    if (!active || !user?.organizationId) return;
    axios.get(`/api/organizations/${user.organizationId}`)
      .then((r) => setCurrency(r.data?.defaultCurrency || ''))
      .catch(() => {});
  }, [active, user?.organizationId]);

  const money = (v) => `${currency} ${num(v).toFixed(2)}`;

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0">
          Record each vendor tax invoice here so its GST can be claimed as input tax credit. Goods you received through a GRN are not booked twice.
        </p>
        <Button variant="primary" onClick={() => setShowForm(true)}>+ Record vendor invoice</Button>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center"><Spinner animation="border" role="status" /></div>
      ) : (
        <Card>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th title="iBigDo's own number for this booking">Our ref</th><th title="The number printed on the vendor's invoice">Vendor's invoice no.</th><th>Vendor</th><th>Invoice date</th><th>Booked</th>
                  <th className="text-end">Taxable</th><th className="text-end">GST</th><th className="text-end">Total</th>
                  <th className="text-end">Credit claimable</th><th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {invoices.map((i) => (
                  <tr key={i.id}>
                    <td><strong>{i.internalNumber}</strong></td>
                    <td>{i.vendorInvoiceNumber}{i.reverseCharge && <Badge bg="warning" text="dark" className="ms-1">RCM</Badge>}</td>
                    <td>{i.vendorName}</td>
                    <td>{formatDate(i.invoiceDate)}</td>
                    <td>{formatDate(i.bookingDate)}</td>
                    <td className="text-end">{money(i.taxableAmount)}</td>
                    <td className="text-end">{money(i.totalTaxAmount)}</td>
                    <td className="text-end">{money(i.totalAmount)}</td>
                    <td className="text-end">{money(i.itcTotal)}</td>
                    <td><Badge bg={i.status === 'POSTED' ? 'success' : 'secondary'}>{i.status === 'POSTED' ? 'Recorded' : 'Cancelled'}</Badge>{i.status === 'POSTED' && <GoodsStatusBadge status={i.goodsStatus} />}</td>
                    <td className="text-nowrap"><Button size="sm" variant="outline-info" onClick={() => setViewing(i)}>View</Button><HistoryButton documentType="PurchaseInvoice" documentId={i.id} label={`Purchase bill ${i.internalNumber}`} className="ms-1" /></td>
                  </tr>
                ))}
                {invoices.length === 0 && (
                  <tr><td colSpan="11" className="text-center text-muted">No vendor invoices recorded yet.</td></tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      <PurchaseNotesList active={active} reloadKey={reloadKey} currency={currency} />

      <PurchaseNoteModal
        invoice={returning}
        currency={currency}
        onHide={() => setReturning(null)}
        onIssued={() => setReloadKey((k) => k + 1)}
      />

      <InvoiceForm
        show={showForm}
        currency={currency}
        onHide={() => setShowForm(false)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
      <InvoiceDetail
        invoice={viewing}
        money={money}
        currency={currency}
        onHide={() => setViewing(null)}
        onChanged={() => { setViewing(null); setReloadKey((k) => k + 1); }}
        onReturn={(inv) => { setViewing(null); setReturning(inv); }}
      />
    </div>
  );
}

/** A GRN line as an invoice line: the received item, quantity and value, at the default GST rate. */
const grnLine = (gl) => ({
  ...emptyLine(),
  description: gl.description || '', itemId: gl.itemId, hsnCode: gl.hsnCode || '',
  quantity: gl.quantity ?? '', unitOfMeasurementId: gl.unitOfMeasurementId, unitSymbol: gl.unitSymbol || '',
  taxableAmount: gl.amount ?? '', unitPrice: rateFromAmount(gl.quantity, gl.amount)
});

function InvoiceForm({ show, currency, onHide, onSaved }) {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [grns, setGrns] = useState([]);
  const [selectedGrns, setSelectedGrns] = useState([]);
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [reverseCharge, setReverseCharge] = useState(false);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([emptyLine()]);
  const [items, setItems] = useState([]);
  const [goodsReceipt, setGoodsReceipt] = useState(emptyGoodsReceipt());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setVendorId(''); setGrns([]); setSelectedGrns([]); setVendorInvoiceNumber('');
    setInvoiceDate(''); setBookingDate(''); setReverseCharge(false); setNotes(''); setLines([emptyLine()]);
    setGoodsReceipt(emptyGoodsReceipt());
    axios.get('/api/inventory/vendors/active')
      .then((r) => setVendors(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('Could not load vendors'));
    axios.get('/api/purchase-invoices/line-items')
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, [show]);

  useEffect(() => {
    setSelectedGrns([]);
    setLines((prev) => mergeGrnLines(prev, [], [], grnLine, emptyLine));
    if (!vendorId) { setGrns([]); return; }
    axios.get(`/api/purchase-invoices/vendor/${vendorId}/uninvoiced-grns`)
      .then((r) => setGrns(Array.isArray(r.data) ? r.data : []))
      .catch(() => setGrns([]));
  }, [vendorId]);

  const vendor = vendors.find((v) => String(v.id) === String(vendorId));
  const grnValue = useMemo(
    () => grns.filter((g) => selectedGrns.includes(g.id)).reduce((s, g) => s + num(g.value), 0), [grns, selectedGrns]);
  const taxable = lines.reduce((s, l) => s + num(l.taxableAmount), 0);
  const estTax = lines.reduce((s, l) => {
    if (l.exact) return s + num(l.cgstAmount) + num(l.sgstAmount) + num(l.igstAmount);
    return s + (num(l.taxableAmount) * num(l.taxRatePercent)) / 100;
  }, 0);
  const difference = round2(taxable - grnValue);

  const setLine = (idx, patch) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  // Quantity, unit price and taxable value work together: typing one fills in the others (see utils/lineAmounts)
  const editAmount = (idx, field, value) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...lineEdit(l, field, value) } : l)));
  const toggleGrn = (id) => {
    const next = selectedGrns.includes(id) ? selectedGrns.filter((x) => x !== id) : [...selectedGrns, id];
    setSelectedGrns(next);
    setLines((prev) => mergeGrnLines(prev, grns, next, grnLineWithRate, emptyLine));
  };
  // The item's usual GST rate from Materials, as the line's rate (still changeable)
  const rateOf = (itemId) => {
    const item = items.find((i) => i.itemId === itemId);
    return item?.gstRate != null ? String(Number(item.gstRate)) : null;
  };
  const pickItem = (idx, item) => setLine(idx, {
    description: item.name, itemId: item.itemId, hsnCode: item.hsnCode || '',
    unitOfMeasurementId: item.unitOfMeasurementId, unitSymbol: item.unitSymbol || '',
    ...(item.gstRate != null ? { taxRatePercent: String(Number(item.gstRate)) } : {})
  });
  const grnLineWithRate = (gl) => {
    const line = grnLine(gl);
    const rate = rateOf(gl.itemId);
    return rate != null ? { ...line, taxRatePercent: rate } : line;
  };

  // Lines picked from the item list are goods; with no GRN linked the bill says where they are (see GoodsReceiptChoice)
  const goodsLines = lines.filter((l) => l.itemId);
  const goodsWithoutGrn = selectedGrns.length === 0 && goodsLines.length > 0;

  const submit = async () => {
    if (!vendorId) { toast.error('Choose the vendor'); return; }
    const goodsProblem = goodsWithoutGrn && goodsReceiptProblem(goodsReceipt, goodsLines);
    if (goodsProblem) { toast.error(goodsProblem); return; }
    try {
      setSaving(true);
      const payload = {
        vendorId: Number(vendorId),
        vendorInvoiceNumber,
        invoiceDate: invoiceDate || null,
        bookingDate: bookingDate || null,
        dueDate: dueDate || null,
        reverseCharge,
        notes,
        grnIds: selectedGrns,
        goodsReceipt: goodsWithoutGrn ? goodsReceipt : null,
        lines: lines.map((l) => ({
          description: l.description,
          itemId: l.itemId || null,
          unitOfMeasurementId: l.unitOfMeasurementId || null,
          hsnCode: l.hsnCode || null,
          quantity: l.quantity === '' ? null : num(l.quantity),
          taxableAmount: l.taxableAmount === '' ? null : num(l.taxableAmount),
          taxRatePercent: num(l.taxRatePercent),
          cgstAmount: l.exact ? num(l.cgstAmount) : null,
          sgstAmount: l.exact ? num(l.sgstAmount) : null,
          igstAmount: l.exact ? num(l.igstAmount) : null,
          itcEligible: l.itcEligible,
          itcIneligibleReason: l.itcEligible ? null : l.itcIneligibleReason
        }))
      };
      const response = await axios.post('/api/purchase-invoices', payload);
      toast.success(`Invoice recorded as ${response.data.internalNumber}`);
      onSaved();
      onHide();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to record the invoice'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton><Modal.Title>Record vendor invoice</Modal.Title></Modal.Header>
      <Modal.Body>
        <Row className="g-3 mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Vendor</Form.Label>
              <Form.Select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                <option value="">-- Choose --</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </Form.Select>
              {vendor && !vendor.taxId && (
                <Form.Text className="text-warning">This vendor has no GSTIN; GST on their invoice can&apos;t be claimed unless it is under reverse charge.</Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Vendor&apos;s invoice number</Form.Label>
              <Form.Control value={vendorInvoiceNumber} maxLength={40} onChange={(e) => setVendorInvoiceNumber(e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Invoice date</Form.Label>
              <DateInput value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Booking date (blank = today)</Form.Label>
              <DateInput value={bookingDate} min={invoiceDate} onChange={(e) => setBookingDate(e.target.value)} />
            </Form.Group>
          </Col>
        </Row>
        <Row className="g-3 mb-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Due date</Form.Label>
              <DateInput value={dueDate} min={invoiceDate || undefined} onChange={(e) => setDueDate(e.target.value)} />
              <Form.Text className="text-muted">
                {(() => {
                  const v = vendors.find((x) => String(x.id) === String(vendorId));
                  if (!v) return 'Blank: worked out from the vendor\u2019s credit days.';
                  const msme = v.msmeCategory === 'MICRO' || v.msmeCategory === 'SMALL';
                  const days = msme ? Math.min(v.creditDays ?? 15, 45) : (v.creditDays ?? 0);
                  return `Blank: ${days} day${days === 1 ? '' : 's'} after the bill date${msme ? ' (MSME: 45 days at most)' : ''}.`;
                })()}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {vendorId && (
          <div className="mb-3">
            <Form.Label>Goods this invoice covers (received GRNs not yet invoiced; ticking one fills in its lines)</Form.Label>
            {grns.length === 0 ? (
              <div className="text-muted small">No uninvoiced GRNs for this vendor. For services or expenses, leave this empty: the whole value is booked.</div>
            ) : (
              <div className="d-flex flex-wrap gap-3">
                {grns.map((g) => (
                  <Form.Check key={g.id} type="checkbox" id={`pi-grn-${g.id}`}
                    label={`${g.grnNumber} (${formatDate(g.grnDate)}) - ${currency} ${num(g.value).toFixed(2)}`}
                    checked={selectedGrns.includes(g.id)} onChange={() => toggleGrn(g.id)} />
                ))}
              </div>
            )}
          </div>
        )}

        <Table bordered size="sm" responsive>
          <thead>
            <tr>
              <th style={{ minWidth: 180 }}>Description</th><th style={{ width: 100 }}>HSN</th><th style={{ width: 90 }}>Qty</th>
              <th style={{ width: 110 }}>Unit price</th><th style={{ width: 130 }}>Taxable value</th><th style={{ width: 80 }}>GST %</th>
              <th style={{ width: 90 }}>Credit?</th><th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => (
              <tr key={idx}>
                <td>
                  <ItemDescriptionInput value={l.description} itemId={l.itemId} items={items}
                    onChange={(text) => setLine(idx, { description: text, itemId: null, unitOfMeasurementId: null, unitSymbol: '' })}
                    onPick={(item) => pickItem(idx, item)} />
                  <Form.Check className="small mt-1" type="checkbox" id={`pi-exact-${idx}`} label="Enter GST amounts as printed on the invoice"
                    checked={l.exact} onChange={(e) => setLine(idx, { exact: e.target.checked })} />
                  {l.exact && (
                    <div className="d-flex gap-2 mt-1">
                      {[['cgstAmount', 'CGST'], ['sgstAmount', 'SGST'], ['igstAmount', 'IGST']].map(([key, label]) => (
                        <Form.Control key={key} size="sm" type="number" min="0" step="0.01" placeholder={label} value={l[key]}
                          onChange={(e) => setLine(idx, { [key]: e.target.value })} />
                      ))}
                    </div>
                  )}
                  {!l.itcEligible && (
                    <Form.Control className="mt-1" size="sm" placeholder="Why no credit? (e.g. blocked under section 17(5))"
                      value={l.itcIneligibleReason} maxLength={100} onChange={(e) => setLine(idx, { itcIneligibleReason: e.target.value })} />
                  )}
                </td>
                <td><Form.Control size="sm" value={l.hsnCode} maxLength={20} onChange={(e) => setLine(idx, { hsnCode: e.target.value })} /></td>
                <td>
                  <Form.Control size="sm" type="number" min="0" step="any" value={l.quantity} onChange={(e) => editAmount(idx, 'quantity', e.target.value)} />
                  {l.unitSymbol && <div className="small text-muted">{l.unitSymbol}</div>}
                </td>
                <td><Form.Control size="sm" type="number" min="0" step="any" value={l.unitPrice} onChange={(e) => editAmount(idx, 'unitPrice', e.target.value)} /></td>
                <td><Form.Control size="sm" type="number" min="0" step="0.01" value={l.taxableAmount} onChange={(e) => editAmount(idx, 'taxableAmount', e.target.value)} /></td>
                <td><Form.Control size="sm" type="number" min="0" max="100" step="any" value={l.taxRatePercent} onChange={(e) => setLine(idx, { taxRatePercent: e.target.value })} /></td>
                <td>
                  <Form.Check type="checkbox" id={`pi-itc-${idx}`} label="Claim" checked={l.itcEligible}
                    onChange={(e) => setLine(idx, { itcEligible: e.target.checked })} />
                </td>
                <td>
                  {lines.length > 1 && (
                    <Button size="sm" variant="outline-danger" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}>&times;</Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button size="sm" variant="outline-secondary" onClick={() => setLines((prev) => [...prev, emptyLine()])}>+ Add line</Button>
        {goodsWithoutGrn && (
          <div className="mt-3">
            <GoodsReceiptChoice value={goodsReceipt} onChange={setGoodsReceipt} uninvoicedGrns={grns.length} document="invoice" idPrefix="pi-goods" />
          </div>
        )}

        <Row className="g-3 mt-2">
          <Col md={6}>
            <Form.Check type="checkbox" id="pi-rcm" label="Reverse charge (the vendor didn't charge GST; you owe it directly)"
              checked={reverseCharge} onChange={(e) => setReverseCharge(e.target.checked)} />
            <Form.Group className="mt-3">
              <Form.Label>Notes (optional)</Form.Label>
              <Form.Control value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
            </Form.Group>
          </Col>
          <Col md={6} className="text-end">
            <div>Taxable value: <strong>{currency} {round2(taxable).toFixed(2)}</strong></div>
            <div>GST: <strong>{currency} {round2(estTax).toFixed(2)}</strong></div>
            <div className="fs-5">Invoice total: <strong>{currency} {round2(taxable + estTax).toFixed(2)}</strong></div>
            {selectedGrns.length > 0 && (
              <Alert variant={Math.abs(difference) < 0.005 ? 'success' : 'warning'} className="py-1 mt-2 mb-0 small text-start">
                GRNs already hold {currency} {round2(grnValue).toFixed(2)} on the payable.
                {Math.abs(difference) < 0.005
                  ? ' The invoice matches, so only the GST is added.'
                  : ` The invoice is ${difference > 0 ? 'above' : 'below'} that by ${currency} ${Math.abs(difference).toFixed(2)}, and the payable is adjusted by the same amount.`}
              </Alert>
            )}
            <div className="text-muted small mt-1">Exact GST is worked out when the invoice is recorded.</div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={saving}>{saving ? 'Recording...' : 'Record invoice'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

function InvoiceDetail({ invoice, money, currency, onHide, onChanged, onReturn }) {
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const [full, setFull] = useState(null);

  useEffect(() => {
    setCancelling(false);
    setReason('');
    setFull(null);
    if (!invoice) return;
    axios.get(`/api/purchase-invoices/${invoice.id}`).then((r) => setFull(r.data)).catch(() => setFull(invoice));
  }, [invoice]);

  const cancel = async () => {
    try {
      await axios.post(`/api/purchase-invoices/${invoice.id}/cancel`, { reason });
      toast.success('Invoice cancelled and its entries reversed');
      onChanged();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to cancel the invoice'));
    }
  };

  const i = full || invoice;
  return (
    <Modal show={Boolean(invoice)} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{i ? `${i.internalNumber} - ${i.vendorInvoiceNumber}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {i && (
          <>
            <Row className="mb-3">
              <Col md={6}>
                <p className="mb-1"><strong>Vendor:</strong> {i.vendorName}</p>
                <p className="mb-1"><strong>Vendor GSTIN:</strong> {i.supplierGstin || '-'}</p>
                <p className="mb-1"><strong>Invoice date:</strong> {formatDate(i.invoiceDate)} <span className="text-muted">(booked {formatDate(i.bookingDate)})</span></p>
                <p className="mb-1"><strong>Supply:</strong> {i.isInterState ? 'Inter-state (IGST)' : 'Same state (CGST + SGST)'}{i.reverseCharge ? ', reverse charge' : ''}</p>
              </Col>
              <Col md={6}>
                <p className="mb-1"><strong>GRNs covered:</strong> {(i.grnNumbers || []).join(', ') || 'none'}<GoodsStatusBadge status={i.goodsStatus} /></p>
                <p className="mb-1"><strong>Value already on payable (GRNs):</strong> {money(i.grnValue)}</p>
                <p className="mb-1"><strong>Added or adjusted by this invoice:</strong> {money(i.bookedValueAdjustment)}</p>
                {i.status === 'CANCELLED' && <p className="mb-1 text-danger"><strong>Cancelled:</strong> {i.cancelReason}</p>}
              </Col>
            </Row>
            <Table bordered size="sm">
              <thead>
                <tr><th>Description</th><th>HSN</th><th className="text-end">Qty</th><th className="text-end">Unit price</th><th className="text-end">Taxable</th><th className="text-end">GST %</th>
                  <th className="text-end">CGST</th><th className="text-end">SGST</th><th className="text-end">IGST</th><th>Credit</th></tr>
              </thead>
              <tbody>
                {(i.lines || []).map((l) => (
                  <tr key={l.id}>
                    <td>{l.description}</td><td>{l.hsnCode || '-'}</td>
                    <td className="text-end">{l.quantity != null ? `${Number(l.quantity)}${l.unitSymbol ? ` ${l.unitSymbol}` : ''}` : '-'}</td>
                    <td className="text-end">{l.quantity != null && Number(l.quantity) > 0 ? `${currency} ${rateText(l.taxableAmount / l.quantity)}` : '-'}</td>
                    <td className="text-end">{money(l.taxableAmount)}</td><td className="text-end">{l.taxRatePercent}%</td>
                    <td className="text-end">{money(l.cgstAmount)}</td><td className="text-end">{money(l.sgstAmount)}</td><td className="text-end">{money(l.igstAmount)}</td>
                    <td>{l.itcEligible ? 'Claimed' : <span title={l.itcIneligibleReason || ''}>Not claimed</span>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="text-end">
              <div>Taxable: <strong>{money(i.taxableAmount)}</strong> &nbsp; GST: <strong>{money(i.totalTaxAmount)}</strong> &nbsp; Total: <strong>{money(i.totalAmount)}</strong></div>
              <div>Input credit claimable: <strong>{money(i.itcTotal)}</strong>{num(i.ineligibleTaxAmount) > 0 && <> &nbsp; Not claimable (cost): <strong>{money(i.ineligibleTaxAmount)}</strong></>}</div>
            </div>
            {i.notes && <p className="mt-2"><strong>Notes:</strong> {i.notes}</p>}
            {cancelling && (
              <Form.Group className="mt-3">
                <Form.Label>Reason for cancelling</Form.Label>
                <Form.Control value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
                <Form.Text className="text-muted">
                  This reverses every entry the invoice made{['RECEIVED_NOW', 'ARRIVED'].includes(i.goodsStatus) ? ', and the GRN its goods came in on (refused if any of that stock has been used)' : ''}. If you already claimed its credit in a filed GSTR-3B, correct that return too.
                </Form.Text>
              </Form.Group>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {i?.status === 'POSTED' && !cancelling && (
          <>
            <GoodsToArriveButton bill={i} onChanged={onChanged} />
            <Button variant="outline-warning" onClick={() => onReturn(i)}>Return note</Button>
            <Button variant="outline-danger" onClick={() => setCancelling(true)}>Cancel invoice</Button>
          </>
        )}
        {cancelling && (
          <>
            <Button variant="secondary" onClick={() => setCancelling(false)}>Keep it</Button>
            <Button variant="danger" onClick={cancel} disabled={!reason.trim()}>Cancel invoice</Button>
          </>
        )}
        {!cancelling && <Button variant="secondary" onClick={onHide}>Close</Button>}
      </Modal.Footer>
    </Modal>
  );
}

export default PurchaseInvoices;
